import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useParams } from 'react-router';
import { keys, useRecord, useReVerify, useSession } from '../api/hooks.js';
import { Button, ButtonAnchor, ButtonLink } from '../components/Button.js';
import { CheckRow } from '../components/CheckRow.js';
import { cx, MONO } from '../components/classes.js';
import { CopyIcon, DownloadIcon } from '../components/Icons.js';
import { Mark } from '../components/Mark.js';
import { ProblemNote, SkeletonRows } from '../components/Notes.js';
import { QrCode } from '../components/QrCode.js';
import { StatusGlyph } from '../components/StatusGlyph.js';
import {
  CHECK_ORDER,
  formatInstant,
  PURPOSE_LABELS,
  resultDetail,
  resultHeadline,
  shortHash,
  titleCase,
} from '../lib/format.js';
import type { Verification } from '../lib/types.js';
import { canIssue, useSessionStore } from '../store/session.js';

function resultTone(r: Verification): string {
  if (!r.entity) return 'text-mute';
  if (r.result.failed > 0) return 'text-crit';
  if (r.result.attention > 0 || r.result.unknown > 0) return 'text-warn';
  return 'text-good';
}

/** Wireframe §7 / Issued artboard: the file, the PDF, the public link — and one rewarding motion. */
export function Record() {
  const { id = '' } = useParams();
  const location = useLocation();
  const justIssued = Boolean((location.state as { issued?: boolean } | null)?.issued);
  const navigate = useNavigate();
  const record = useRecord(id);
  const session = useSession();
  const reverify = useReVerify();
  const qc = useQueryClient();
  const accountName = useSessionStore((s) => s.accountName);
  const role = useSessionStore((s) => s.role);
  const [copied, setCopied] = useState(false);

  if (record.isPending) {
    return (
      <main className="flex justify-center px-12 pt-16 pb-16">
        <div className="w-[720px] max-w-full">
          <SkeletonRows
            rows={5}
            className="rounded-issued border border-line bg-surface"
          />
        </div>
      </main>
    );
  }
  if (record.isError) {
    return (
      <main className="flex justify-center px-12 pt-16 pb-16">
        <div className="w-[720px] max-w-full">
          <ProblemNote error={record.error} />
        </div>
      </main>
    );
  }
  const r = record.data.data;
  const ent = session.data?.entitlements;
  const subLine = justIssued
    ? `Filed to your records.${ent && ent.remaining !== null ? ` ${ent.remaining} left ${ent.quota_window === 'day' ? 'today' : 'this month'}.` : ''}`
    : `Issued ${formatInstant(r.issued_at)}`;
  const verifyPath = r.report_url.replace(/^https?:\/\//, '');
  const checks = CHECK_ORDER.filter((name) => r.checks[name]);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(r.report_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };
  const onDownload = () => {
    // The first download stamps report_sha256 on the record; show it once it lands.
    setTimeout(() => void qc.invalidateQueries({ queryKey: keys.record(id) }), 1500);
  };
  const mayIssue = canIssue(role);
  // A fresh record for the same subject — purpose and reference carried over,
  // never an idempotent replay (see useReVerify).
  const reVerify = () => {
    if (!r.entity) return;
    reverify.mutate(
      { entity_id: r.entity.id, purpose: r.purpose, reference: r.reference },
      {
        onSuccess: (env) =>
          navigate(`/records/${env.data.id}`, { state: { issued: true } }),
      },
    );
  };

  return (
    <main className="flex justify-center px-12 pt-16 pb-16">
      <div className="flex w-[720px] max-w-full flex-col gap-6">
        <div className={cx('flex items-center gap-3.5', justIssued && 'animate-fade')}>
          <StatusGlyph
            status={r.entity ? 'pass' : 'unknown'}
            size="lg"
            label={r.entity ? 'Issued' : 'No match'}
          />
          <div className="flex flex-col">
            <span className="text-[24px] font-semibold tracking-[-0.01em]">
              {justIssued
                ? r.entity
                  ? 'Record issued'
                  : 'No-match record filed'
                : r.entity
                  ? 'Verification record'
                  : 'No-match record'}
            </span>
            <span className="text-[14px] text-mute">{subLine}</span>
          </div>
        </div>

        <section
          className={cx(
            'overflow-hidden rounded-issued border border-line bg-surface shadow-issued',
            justIssued && 'animate-rise',
          )}
          aria-label="Verification record"
        >
          <div className="flex items-start justify-between border-b border-line px-8 py-7">
            <div className="flex items-center gap-3">
              <Mark />
              <div className="flex flex-col">
                <span className="text-[15px] font-semibold">
                  Dira verification record
                </span>
                <span className="text-[13px] text-mute">
                  Issued {formatInstant(r.issued_at)}
                  {accountName ? ` to ${accountName}` : ''}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className={cx(MONO, 'text-[13px] font-medium')}>{r.id}</span>
              <span className="text-[12px] text-mute">
                {[r.purpose ? PURPOSE_LABELS[r.purpose] : null, r.reference]
                  .filter(Boolean)
                  .join(' · ') || 'No purpose recorded'}
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-5 px-8 py-6">
            <div className="flex items-end justify-between gap-6">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-[13px] text-mute">
                  Subject as submitted: “{r.subject}”
                </span>
                <span className="text-[22px] font-semibold tracking-[-0.01em]">
                  {r.entity ? titleCase(r.entity.name) : 'Not on the register'}
                </span>
                <span className={cx(MONO, 'text-[12px] text-mute')}>
                  {r.entity
                    ? [
                        r.entity.dira_id ?? r.entity.id,
                        r.entity.match_confidence !== null
                          ? `match confidence ${r.entity.match_confidence.toFixed(2)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : 'PACRA and ZRA searched; nothing matched at the threshold'}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end">
                <span className={cx('text-[20px] font-semibold', resultTone(r))}>
                  {r.entity ? resultHeadline(r.result) : 'No match'}
                </span>
                {resultDetail(r.result) ? (
                  <span className="text-[13px] font-medium text-warn">
                    {resultDetail(r.result)}
                  </span>
                ) : null}
              </div>
            </div>
            {checks.length > 0 ? (
              <div className="flex flex-col">
                {checks.map((name, i) => (
                  <CheckRow
                    key={name}
                    name={name}
                    check={r.checks[name]!}
                    first={i === 0}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-btn bg-sunk px-4 py-3.5 text-[13px] text-mute">
                No registered entity matched the subject at the required confidence, so no
                checks were run. This record is the proof the search was done.
              </div>
            )}
            {r.cached || r.sources_failed.length ? (
              <div className="text-[13px] text-mute">
                {r.sources_failed.length
                  ? `${r.sources_failed.map((s) => s.toUpperCase()).join(', ')} did not answer at issue; those facts were served from the last stored read.`
                  : 'Served from records read within the last 15 minutes.'}
              </div>
            ) : null}
            <div className="flex items-end justify-between gap-6 border-t border-line pt-5">
              <div className="flex flex-col gap-1 text-[13px]">
                <span className="text-mute">
                  Anyone with this link can confirm the record is genuine
                </span>
                <span className={MONO}>{verifyPath}</span>
                <span className={cx(MONO, 'text-[12px] text-mute')}>
                  {r.report_sha256
                    ? `sha256 ${shortHash(r.report_sha256)}`
                    : 'sha256 — set when the PDF is first rendered'}
                </span>
              </div>
              <QrCode value={r.report_url} label="Verification QR code" />
            </div>
          </div>
        </section>

        <div className="flex gap-3">
          <ButtonAnchor
            href={`/v1/verifications/${encodeURIComponent(r.id)}/report.pdf`}
            variant="primary"
            onClick={onDownload}
          >
            <DownloadIcon />
            Download PDF
          </ButtonAnchor>
          <Button onClick={() => void copyLink()}>
            <CopyIcon />
            {copied ? 'Copied' : 'Copy verify link'}
          </Button>
          {r.entity ? (
            <Button
              onClick={reVerify}
              disabled={reverify.isPending || !mayIssue}
              title={
                mayIssue
                  ? 'Issue a fresh record for this subject'
                  : 'Your role is read-only (auditor)'
              }
            >
              {reverify.isPending ? 'Issuing…' : 'Re-verify'}
            </Button>
          ) : null}
          <span className="grow" />
          <ButtonLink to="/watchlist">Watch for changes</ButtonLink>
        </div>
        {reverify.isError ? <ProblemNote error={reverify.error} /> : null}
        <p className="m-0 max-w-[60ch] text-[13px] leading-relaxed text-mute">
          This record states what the public registers returned at the times shown. It is
          not a credit assessment or an opinion on whether to trade.
        </p>
      </div>
    </main>
  );
}
